import { Modal } from "@mui/material";
import { lazyComponent } from "@redotech/react-util/component";
import AsteriskIcon from "@redotech/redo-icons/asterisk-01_filled.svg";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import classNames from "classnames";
import { memo, useCallback, useEffect, useState } from "react";
import { RedoBadge } from "../../../arbiter-components/badge/redo-badge";
import { RedoButton } from "../../../arbiter-components/buttons/redo-button";
import { RedoIcon } from "../../../arbiter-components/icon/redo-icon";
import { Flex } from "../../../flex";
import { Text } from "../../../text";
import * as chatStyles from "../styles/slide-out-chat-modal.module.css";

const ChatWithThread = lazyComponent(async () => {
  const { ChatWithThread } = await import("./chat-with-thread");
  return ChatWithThread;
});

// This chat must be wrapped in an AiChatProvider
export const AiSlideOutChat = memo(function AiSlideOutChat({
  open,
  onClose,
  belowChatButtons,
  title,
  isBeta,
  size = "sm",
}: {
  open: boolean;
  onClose: () => void;
  belowChatButtons?: { text: string; onClick: () => void }[];
  title: string;
  isBeta?: boolean;
  size?: "sm" | "lg";
}) {
  // State to handle the exit animation
  const [isClosing, setIsClosing] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);

  const handleClose = useCallback(() => {
    setIsClosing((prevIsClosing) => {
      if (prevIsClosing) return true; // Already closing, don't do anything

      setTimeout(() => {
        setModalOpen(false);
        setIsClosing(false);
        onClose();
      }, 400);

      return true; // Set to closing
    });
  }, [onClose]);

  useEffect(() => {
    if (open && !modalOpen) {
      setIsClosing(false);
      setModalOpen(true);
    } else if (!open && modalOpen && !isClosing) {
      handleClose();
    }
  }, [open, modalOpen, isClosing, handleClose]);
  return (
    <Modal
      onClose={() => {
        handleClose();
      }}
      open={modalOpen}
      sx={{ "& .MuiBackdrop-root": { backgroundColor: "transparent" } }}
    >
      <div
        className={classNames(
          isClosing
            ? chatStyles.aiChatCartContainerExit
            : chatStyles.aiChatCartContainer,
          size === "sm"
            ? chatStyles.aiChatContainerSmall
            : chatStyles.aiChatContainerLarge,
        )}
      >
        <Flex
          bgColor="secondary"
          borderColor="primary"
          borderStyle="solid"
          borderWidth="1px"
          dir="column"
          height="full"
          overflow="hidden"
          radius="md"
          shadow="lg"
        >
          <Flex
            align="center"
            flexDirection="row"
            justify="space-between"
            px="lg"
            py="md"
          >
            <Flex align="center">
              <RedoIcon
                className={chatStyles.aiChatIcon}
                color="brand"
                Icon={AsteriskIcon}
                size={16}
              />
              <Text fontSize="xs" fontWeight="medium">
                {title}
              </Text>
              {isBeta && <RedoBadge color="orange" size="sm" text="Beta" />}
            </Flex>
            <RedoButton
              data-testid="chat-close-button"
              hierarchy="tertiary"
              IconLeading={CloseIcon}
              onClick={handleClose}
              size="md"
            />
          </Flex>

          <ChatWithThread
            belowChatButtons={belowChatButtons}
            style="high-contrast"
          />
        </Flex>
      </div>
    </Modal>
  );
});
